import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { paragraphsWithStyle, readDocument } from "@/office/word/document";
import { hasMeaningfulAltText, picturesWithoutAltText, readPictures } from "@/office/word/media";

export const lektionBilderAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Beschriftungen und Abschlussübung",
  summary: "Bilder mit Beschriftung versehen – die Vorbereitung fürs Abbildungsverzeichnis.",
  durationMinutes: 10,
  learningGoals: [
    "Bildbeschriftungen über die Beschriftungsfunktion einfügen",
    "Ein Bild vollständig und barrierefrei einbinden",
  ],
  steps: [
    {
      id: "beschriftung",
      title: "Beschriftungen einfügen",
      explanation: [
        "Unter einem Bild steht üblicherweise eine Beschriftung: „Abbildung 1: " +
          "Umsatzentwicklung 2025“. Auch die tippt man nicht von Hand.",
        "Rechtsklick auf das Bild → „Beschriftung einfügen“ erzeugt einen Absatz in der " +
          "Formatvorlage „Beschriftung“, in dem die Nummer ein Feld ist. Word zählt " +
          "automatisch weiter, und wenn du später eine Abbildung in der Mitte einfügst, " +
          "nummeriert es alles neu.",
        "Damit ist auch die Grundlage für zwei Dinge gelegt, die im Aufbauteil dieses " +
          "Kurses kommen: das Abbildungsverzeichnis und Querverweise im Text („siehe " +
          "Abbildung 3“), die selbst dann noch stimmen, wenn sich die Nummern ändern.",
      ],
      keyPoints: [
        "Rechtsklick auf das Bild → Beschriftung einfügen.",
        "Die Nummer ist ein Feld und zählt automatisch weiter.",
        "Die Beschriftung nutzt die Formatvorlage „Beschriftung“.",
      ],
      task: {
        instruction: "Füge unter deinem Bild eine Beschriftung über die Beschriftungsfunktion ein.",
        hint: "Nicht von Hand tippen – Rechtsklick auf das Bild → „Beschriftung einfügen“.",
        check: async () => {
          const snapshot = await readDocument();
          const beschriftungen = paragraphsWithStyle(snapshot, "Caption");

          if (beschriftungen.length === 0) {
            return fail(
              "Es wurde noch kein Absatz in der Formatvorlage „Beschriftung“ gefunden.",
              "Eine getippte Zeile zählt nicht. Nutze Rechtsklick → „Beschriftung einfügen“.",
            );
          }
          return pass(
            `Die Beschriftung sitzt („${beschriftungen[0].text.slice(0, 40)}“) – damit ist das ` +
              "Abbildungsverzeichnis später ein Klick.",
          );
        },
      },
    },
    {
      id: "abschluss",
      title: "Abschlussübung",
      explanation: [
        "Bringe alles zusammen: ein eingefügtes Bild mit sinnvollem Alternativtext und " +
          "einer richtigen Beschriftung darunter.",
      ],
      keyPoints: [
        "Alternativtext für Screenreader.",
        "Beschriftung über die Beschriftungsfunktion.",
        "Vor dem Versand die Bilder komprimieren.",
      ],
      task: {
        instruction:
          "Dein Dokument soll mindestens ein Bild enthalten, das einen beschreibenden " +
          "Alternativtext hat und unter dem eine echte Beschriftung steht.",
        hint: "Die Beschriftung erkennst du daran, dass sie die Formatvorlage „Beschriftung“ trägt.",
        check: async () => {
          const bilder = await readPictures();
          const snapshot = await readDocument();
          const beschriftungen = paragraphsWithStyle(snapshot, "Caption");

          if (bilder.length === 0) {
            return fail("Im Dokument ist kein Bild eingefügt.", "Füge zuerst ein Bild ein.");
          }

          const ohne = picturesWithoutAltText(bilder);
          return fromCriteria(
            [
              criterion(`Mindestens ein Bild im Dokument (gefunden: ${bilder.length})`, bilder.length >= 1),
              criterion(`Kein Bild ohne Alternativtext (ohne: ${ohne.length})`, ohne.length === 0),
              criterion(
                "Mindestens ein Alternativtext ist beschreibend",
                bilder.some(hasMeaningfulAltText),
              ),
              criterion(
                `Eine Beschriftung ist vorhanden (gefunden: ${beschriftungen.length})`,
                beschriftungen.length >= 1,
              ),
            ],
            {
              success: "Das Bild ist vollständig eingebunden – auch für Menschen, die es nicht sehen können.",
              failure: "Fast geschafft. Diese Punkte fehlen noch:",
              hint: "Der Alternativtext wird über den Rechtsklick gesetzt, die Beschriftung über „Beschriftung einfügen“ – zwei verschiedene Dinge.",
            },
          );
        },
      },
    },
  ],
};
